/**
 * Invite Page Logic
 *
 * Handles user invitation acceptance and password setup
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import { injectLayout } from '../app/layout.js';
import { applyTheme } from '../app/theme.js';
import { formSubmitHandler } from '../ui/forms.js';

/**
 * Get the token from the URL
 */
function getToken(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get('token') || '';
}

/**
 * Show the error view with a message and a login link
 */
function showError(message: string): void {
  const errorView = document.getElementById('invite-error-view');
  const formView = document.getElementById('invite-form-view');
  if (formView) formView.style.display = 'none';
  if (errorView) {
    errorView.style.display = 'block';
    const errorMsg = document.getElementById('invite-error-message');
    if (errorMsg) errorMsg.textContent = message;
  }
}

/**
 * Handle password form submission
 */
async function handleSetPassword(): Promise<void> {
  const passwordInput = document.getElementById('password') as HTMLInputElement;
  const confirmInput = document.getElementById('confirm') as HTMLInputElement;
  const errorDiv = document.getElementById('invite-error');
  if (errorDiv) errorDiv.textContent = '';

  const password = passwordInput?.value || '';
  const confirm = confirmInput?.value || '';

  if (password !== confirm) {
    if (errorDiv) errorDiv.textContent = 'Passwords do not match';
    return;
  }

  if (password.length < 8) {
    if (errorDiv) errorDiv.textContent = 'Password must be at least 8 characters long';
    return;
  }

  const token = getToken();

  const result = await apiCall(api.completeInvite(token, password), undefined, (err) => {
    // Token was valid at page load but failed at submit — could have
    // expired in the meantime or been consumed in another tab.
    if (err.code === 'INVALID_TOKEN') {
      showError('This invitation link has expired. Please contact an administrator for a new invitation.');
      return;
    }
    if (errorDiv) errorDiv.textContent = err.message;
  });
  if (result === null) return;

  // Show success view
  const formView = document.getElementById('invite-form-view');
  const successView = document.getElementById('invite-success-view');
  if (formView) formView.style.display = 'none';
  if (successView) successView.style.display = 'block';
}

/**
 * Initialize the invite page
 */
export async function initInvitePage(): Promise<void> {
  applyTheme();
  // Inject minimal layout
  injectLayout(null);

  const token = getToken();
  const formView = document.getElementById('invite-form-view');

  if (!token) {
    showError('No invitation token provided.');
    return;
  }

  // Validate the token before showing the form
  const check = await apiCall(api.checkInvite(token), undefined, () => {
    // Network/server error — show the form anyway and let submission handle it
  });

  if (check && !check.valid) {
    if (check.reason === 'not_found') {
      // Token already used or never existed — redirect straight to login
      window.location.href = '/login';
      return;
    }
    // Token expired — show message with login link
    showError('This invitation link has expired. Please contact an administrator for a new invitation.');
    return;
  }

  // Token is valid (or check failed) — show the form
  if (formView) formView.style.display = 'block';

  const form = document.getElementById('invite-form');
  if (form) {
    formSubmitHandler(form, handleSetPassword);
  }
}

// Export to window
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).initInvitePage = initInvitePage;
}
